// Image standards for deal photos — Lot B.2 (2026-05-16 client feedback).
// Source of truth for the dimension / ratio constraints applied client-side
// at upload time (DealPhotoUploader) and at insert time (uploadDealPhoto).
// Edge functions in supabase/functions/ duplicate the same constants (no
// runtime sharing between src/ and supabase/functions/ — Deno).

/** Cover (position 0) must be at least 1600×1200 px. Backed by Retina /
 * desktop hero rendering where the gallery hero spans up to 1600px wide
 * before the lightbox kicks in. */
export const IMAGE_MIN_COVER_WIDTH = 1600;
export const IMAGE_MIN_COVER_HEIGHT = 1200;

/** Gallery photos (positions 1-14) must be at least 1200×900 px — smaller
 * than the cover but still comfortable on desktop thumbs. */
export const IMAGE_MIN_GALLERY_WIDTH = 1200;
export const IMAGE_MIN_GALLERY_HEIGHT = 900;

/** Aspect ratio bounds (width / height). 1.0 = square. 2.5 ≈ ultra-panoramic
 * but still usable in the gallery hero (which renders at 2.2:1). Portrait
 * (ratio < 1.0) is rejected because the card containers are landscape 4:3. */
export const IMAGE_MIN_RATIO = 1.0;
export const IMAGE_MAX_RATIO = 2.5;

export type ImageRole = 'cover' | 'gallery';

export type ImageValidationErrorCode = 'tooSmallCover' | 'tooSmallGallery' | 'ratioOutOfBounds';

export type ImageValidationError = {
  code: ImageValidationErrorCode;
  width: number;
  height: number;
  ratio: number;
};

export type ImageValidationResult = { ok: true } | { ok: false; error: ImageValidationError };

/** Validate raw pixel dimensions against the standards above. Returns a
 * structured error object so the caller can map to i18n keys + interpolate
 * the actual values into the toast. */
export function validateImageDimensions(
  width: number,
  height: number,
  role: ImageRole,
): ImageValidationResult {
  if (width <= 0 || height <= 0) {
    return {
      ok: false,
      error: {
        code: role === 'cover' ? 'tooSmallCover' : 'tooSmallGallery',
        width,
        height,
        ratio: 0,
      },
    };
  }
  const ratio = width / height;
  if (ratio < IMAGE_MIN_RATIO || ratio > IMAGE_MAX_RATIO) {
    return { ok: false, error: { code: 'ratioOutOfBounds', width, height, ratio } };
  }
  if (role === 'cover') {
    if (width < IMAGE_MIN_COVER_WIDTH || height < IMAGE_MIN_COVER_HEIGHT) {
      return { ok: false, error: { code: 'tooSmallCover', width, height, ratio } };
    }
  } else if (width < IMAGE_MIN_GALLERY_WIDTH || height < IMAGE_MIN_GALLERY_HEIGHT) {
    return { ok: false, error: { code: 'tooSmallGallery', width, height, ratio } };
  }
  return { ok: true };
}

/** Map a browser-reported `File.type` to one of the 3 MIME values the
 * `deal_photos_mime_type_check` constraint accepts. Anything else returns
 * null (the caller then inserts NULL, which is allowed during the B.1 → B.2
 * transition window). `image/jpg` is folded into `image/jpeg` to match the
 * IANA-canonical form. */
export function normalizeMimeType(
  rawType: string,
): 'image/jpeg' | 'image/png' | 'image/webp' | null {
  const lower = rawType.toLowerCase();
  if (lower === 'image/jpeg' || lower === 'image/jpg') return 'image/jpeg';
  if (lower === 'image/png') return 'image/png';
  if (lower === 'image/webp') return 'image/webp';
  return null;
}
